import { ChangeDetectionStrategy, Component, ElementRef, OnInit, computed, effect, inject, signal, viewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { SelectValueSyncDirective } from '../../../../../../../shared/directives/select-value-sync.directive';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { WorkflowNodeConfigBase } from './node-config-base';
import { NOTIF_CHANNEL_OPTIONS, NOTIF_RECIPIENT_TYPE_OPTIONS } from './config-options';
import { EntitySelectComponent, EntitySelectPage } from '../../../../../../../shared/components/entity-select/entity-select.component';
import { WorkflowVariablePickerComponent } from '../variable-picker/variable-picker.component';
import { RichTextEditorComponent } from '../../../../../../../shared/components/rich-text-editor/rich-text-editor.component';
import { AvailableVariableNamespace } from '../../../../../../templates/models/template.model';
import { TemplateApiService } from '../../../../../../templates/services/template-api.service';
import { WorkflowEmailTemplateApiService, EmailTemplateResponse } from '../../../../services/workflow-email-template-api.service';
import { WorkflowPropertySelectComponent } from './property-select.component';
import { WorkflowRecipientPickerComponent } from './recipient-picker.component';
import { buildPropertyOptions } from '../../../models/workflow-variable-catalog';
import {
  UserDirectoryApiService,
  DirectoryUser,
  directoryUserLabel
} from '../../../../../../../shared/services/user-directory-api.service';
import { ChatTeamLookupApiService } from '../../../../../../chatflows/services/chat-team-lookup-api.service';
import { TeamLookupResponse } from '../../../../../../chatflows/models/agent-lookup.model';
import { WORKFLOW_OBJECT_TYPE_LABELS } from '../../../../models/workflow.model';

/** Destinatarios "Para" de una acción de email, ya normalizados desde cualquier forma histórica. */
interface WorkflowRecipientsModel {
  includeRecordContact: boolean;
  /** Responsable del registro, resuelto en cada ejecución en vez de congelar un usuario. */
  includeRecordOwner: boolean;
  /** Seguidores del ticket (usuarios del CRM). Solo aplica a workflows de Ticket. */
  includeFollowers: boolean;
  emailProperty: string;
  userIds: number[];
  emails: string[];
}

/** Destinatarios de la notificación interna, ya combinables. */
interface NotifRecipientsModel {
  includeOwner: boolean;
  includeFollowers: boolean;
  userIds: number[];
  teamIds: number[];
  emails: string[];
  /** Claves de los pasos de los que se toma el usuario resultante. */
  stepUserKeys: string[];
}

/** Lista de direcciones de CC / CCO, ya normalizada. */
interface WorkflowAddressListModel {
  userIds: number[];
  emails: string[];
}

/**
 * ACTION → Comunicación: SEND_MARKETING_EMAIL, SEND_ONE_TO_ONE_EMAIL, SEND_INTERNAL_NOTIFICATION,
 * SEND_TEAMS_NOTIFICATION, SEND_SLACK_NOTIFICATION, SEND_SMS, SEND_WHATSAPP.
 *
 * Destinatarios de email (SEND_ONE_TO_ONE_EMAIL / SEND_MARKETING_EMAIL): la UI ya no obliga a
 * adivinar el nombre de una propiedad. Se declaran de forma explícita y combinable —
 * contacto del registro + usuarios del CRM + correos sueltos — sobre la forma canónica
 * `recipients: { includeRecordContact, emailProperty, userIds, emails }` y
 * `cc` / `bcc: { userIds, emails }`. Las formas antiguas (`recipient.emailProperty`,
 * `cc`/`bcc` como arrays o strings) SE LEEN y se migran a la canónica al primer cambio.
 */
@Component({
  selector: 'app-wf-communication-action-config',
  standalone: true,
  imports: [
    CommonModule,
    SelectValueSyncDirective,
    EntitySelectComponent,
    WorkflowVariablePickerComponent,
    WorkflowPropertySelectComponent,
    WorkflowRecipientPickerComponent,
    RichTextEditorComponent
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @switch (subtype()) {
      @case ('SEND_MARKETING_EMAIL') {
        <div class="space-y-4">
          <div>
            <label [class]="labelClass">Plantilla de correo</label>
            <app-entity-select
              [value]="num('emailTemplateId') || null" [displayText]="templateName()"
              [searchFn]="templateSearch" [labelFn]="templateLabel" [sublabelFn]="templateSublabel" [disabled]="readOnly()"
              placeholder="Buscar plantilla…"
              (valueChange)="patchConfig({ emailTemplateId: $event })" (selected)="onTemplateSelected($event)"></app-entity-select>
          </div>
          <div>
            <div class="mb-1.5 flex items-center justify-between">
              <label class="text-xs font-medium text-gray-700 dark:text-gray-300">Asunto (si no usas plantilla)</label>
              <app-wf-variable-picker [target]="subjectField()?.nativeElement ?? null" [disabled]="readOnly()"
                                      (valueChange)="patchConfig({ subject: $event })" />
            </div>
            <input #subjectInput type="text" [class]="fieldClass" [value]="str('subject')" [disabled]="readOnly()"
                   (input)="patchConfig({ subject: inputValue($event) })" />
          </div>
          <div>
            <label [class]="labelClass">Cuerpo (opcional)</label>
            <app-rich-text-editor
              [value]="bodyHtml()" [disabled]="readOnly()" minHeight="200px"
              [variableNamespaces]="variableNamespaces()" [uploadHandler]="imageUploadHandler"
              placeholder="Escribe el contenido del correo…"
              (valueChange)="patchConfig({ body: $event })" />
          </div>
          <!--
            Aquí había tres campos —«Nombre remitente», «Correo remitente» y «Responder a»— que el
            motor no lee NUNCA: el envío sale siempre de la conexión de correo del usuario
            remitente (Gmail/Outlook/SMTP), que es quien fija el From, y EmailDispatchRequest ni
            siquiera tiene esos campos. Rellenarlos no cambiaba nada del correo enviado.
          -->
          <p class="rounded-lg bg-gray-50 p-3 text-[11px] leading-relaxed text-gray-600 dark:bg-white/5 dark:text-gray-400">
            El correo se envía desde la cuenta conectada del responsable del registro (o del responsable del
            workflow si no tiene). El remitente lo fija esa cuenta.
          </p>

          <!-- Destinatarios: en marketing el "Para" SIEMPRE es el contacto del registro. -->
          <div class="space-y-3 rounded-lg border border-gray-200 p-3 dark:border-gray-800">
            <div>
              <p class="text-xs font-semibold text-gray-700 dark:text-gray-200">Destinatarios</p>
              <p class="mt-0.5 text-[11px] text-gray-400 dark:text-gray-500">
                El correo de marketing se envía únicamente al contacto del registro inscrito. Para enviar a usuarios
                del CRM o a correos sueltos usa la acción «Enviar correo 1 a 1».
              </p>
            </div>
            <div class="flex items-start gap-2 rounded-lg bg-gray-50 px-2 py-1.5 text-[11px] text-gray-600 dark:bg-gray-800 dark:text-gray-300">
              <i class="fa-solid fa-user mt-0.5 text-[10px] opacity-70"></i>
              <span>
                <span class="font-medium">Para: contacto del registro</span>
                @if (!isContactWorkflow()) {
                  <span class="block text-gray-500 dark:text-gray-400">
                    Este workflow es de {{ objectTypeLabel() }}: se usará el contacto asociado del registro. Si no lo
                    tiene, no habrá destinatario y el paso fallará.
                  </span>
                }
              </span>
            </div>
            <ng-container [ngTemplateOutlet]="ccBlock" />
            <ng-container [ngTemplateOutlet]="bccBlock" />
            <ng-container [ngTemplateOutlet]="advancedRecipientBlock" />
          </div>

          <label class="flex items-center gap-2 text-xs text-gray-700 dark:text-gray-300">
            <input type="checkbox" class="rounded border-gray-300" [checked]="bool('respectSubscription', true)" [disabled]="readOnly()"
                   (change)="patchConfig({ respectSubscription: checkboxValue($event) })" />
            Respetar preferencias de suscripción
          </label>
          <ng-container [ngTemplateOutlet]="continueOnError" />
        </div>
      }

      @case ('SEND_ONE_TO_ONE_EMAIL') {
        <div class="space-y-4">

          <!-- Destinatarios: contacto del registro + usuarios del CRM + correos sueltos (combinables). -->
          <div class="space-y-3 rounded-lg border border-gray-200 p-3 dark:border-gray-800">
            <div>
              <p class="text-xs font-semibold text-gray-700 dark:text-gray-200">Destinatarios (Para)</p>
              <p class="mt-0.5 text-[11px] text-gray-400 dark:text-gray-500">
                Puedes combinar las tres opciones: el correo llega a todos los destinatarios de la lista.
              </p>
            </div>

            <label class="flex items-start gap-2 text-xs text-gray-700 dark:text-gray-300">
              <input type="checkbox" class="mt-0.5 rounded border-gray-300"
                     [checked]="recipients().includeRecordContact" [disabled]="readOnly()"
                     (change)="writeRecipients({ includeRecordContact: checkboxValue($event) })" />
              <span>
                Contacto del registro
                <span class="block text-[11px] text-gray-400 dark:text-gray-500">
                  Se usa el correo del contacto que disparó el workflow. Es el único destinatario que queda
                  registrado en el timeline del contacto.
                </span>
              </span>
            </label>

            <!--
              Destinatarios que se resuelven en CADA ejecución. Elegir a la persona en una lista
              fija funciona hasta que cambia el responsable del registro: entonces el workflow
              sigue escribiendo al anterior sin que nadie se entere.
            -->
            <label class="flex items-start gap-2 text-xs text-gray-700 dark:text-gray-300">
              <input type="checkbox" class="mt-0.5 rounded border-gray-300"
                     [checked]="recipients().includeRecordOwner" [disabled]="readOnly()"
                     (change)="writeRecipients({ includeRecordOwner: checkboxValue($event) })" />
              <span>
                Responsable del registro
                <span class="block text-[11px] text-gray-400 dark:text-gray-500">
                  Se resuelve al ejecutarse: si cambia el responsable, el aviso va a quien lo sea en ese momento.
                </span>
              </span>
            </label>

            @if (isTicketWorkflow()) {
              <label class="flex items-start gap-2 text-xs text-gray-700 dark:text-gray-300">
                <input type="checkbox" class="mt-0.5 rounded border-gray-300"
                       [checked]="recipients().includeFollowers" [disabled]="readOnly()"
                       (change)="writeRecipients({ includeFollowers: checkboxValue($event) })" />
                <span>
                  Seguidores del ticket
                  <span class="block text-[11px] text-gray-400 dark:text-gray-500">
                    Los usuarios que siguen el ticket. Si no tiene seguidores, se omite y el envío continúa.
                  </span>
                </span>
              </label>
            }

            @if (recipients().includeRecordContact && !isContactWorkflow()) {
              <p class="rounded-lg bg-warning-50 px-2 py-1.5 text-[11px] text-warning-700 dark:bg-warning-500/10 dark:text-warning-400">
                Este workflow es de {{ objectTypeLabel() }}: se usará el contacto asociado del registro. Si el registro
                no tiene contacto asociado, este destinatario se omite y el envío continúa con el resto.
              </p>
            }

            <div>
              <p class="mb-1.5 text-[11px] font-medium text-gray-600 dark:text-gray-400">
                Usuarios del CRM y correos sueltos
              </p>
              <app-wf-recipient-picker
                [userIds]="recipients().userIds" [emails]="recipients().emails" [disabled]="readOnly()"
                (userIdsChange)="writeRecipients({ userIds: $event })"
                (emailsChange)="writeRecipients({ emails: $event })" />
            </div>

            @if (!hasAnyRecipient()) {
              <p class="rounded-lg bg-error-50 px-2 py-1.5 text-[11px] text-error-600 dark:bg-error-500/10 dark:text-error-400">
                Configura al menos un destinatario: el contacto del registro, el responsable, los seguidores del ticket, un usuario del CRM o un correo suelto.
              </p>
            }

            <ng-container [ngTemplateOutlet]="ccBlock" />
            <ng-container [ngTemplateOutlet]="bccBlock" />
            <ng-container [ngTemplateOutlet]="advancedRecipientBlock" />
          </div>

          <div>
            <div class="mb-1.5 flex items-center justify-between">
              <label class="text-xs font-medium text-gray-700 dark:text-gray-300">Asunto</label>
              <app-wf-variable-picker [target]="subjectField()?.nativeElement ?? null" [disabled]="readOnly()"
                                      (valueChange)="patchConfig({ subject: $event })" />
            </div>
            <input #subjectInput type="text" [class]="fieldClass" [value]="str('subject')" [disabled]="readOnly()"
                   (input)="patchConfig({ subject: inputValue($event) })" />
          </div>
          <div>
            <label [class]="labelClass">Cuerpo</label>
            <app-rich-text-editor
              [value]="bodyHtml()" [disabled]="readOnly()" minHeight="220px"
              [variableNamespaces]="variableNamespaces()" [uploadHandler]="imageUploadHandler"
              placeholder="Escribe el contenido del correo…"
              (valueChange)="patchConfig({ body: $event })" />
          </div>
          <div>
            <label class="flex items-center gap-2 text-xs text-gray-700 dark:text-gray-300">
              <input type="checkbox" class="rounded border-gray-300" [checked]="bool('fromOwner', true)" [disabled]="readOnly()"
                     (change)="patchConfig({ fromOwner: checkboxValue($event) })" />
              Enviar desde el responsable del registro
            </label>
            <p [class]="hintClass">
              Si lo desmarcas, el correo sale desde el responsable del workflow. En ambos casos hace falta que ese
              usuario tenga una cuenta de correo conectada.
            </p>
          </div>
          <ng-container [ngTemplateOutlet]="continueOnError" />
        </div>
      }

      @case ('SEND_INTERNAL_NOTIFICATION') {
        <div class="space-y-4">
          <!--
            Los destinatarios son COMBINABLES: avisar de algo suele implicar al responsable Y a
            los seguidores, no a uno u otro. La forma antigua (un solo tipo) se migra al tocar
            cualquier casilla.
          -->
          <div class="space-y-2">
            <label class="text-xs font-medium text-gray-700 dark:text-gray-300">Destinatarios</label>

            <label class="flex items-start gap-2 text-xs text-gray-700 dark:text-gray-300">
              <input type="checkbox" class="mt-0.5 rounded border-gray-300"
                     [checked]="notifRecipients().includeOwner" [disabled]="readOnly()"
                     (change)="writeNotifRecipients({ includeOwner: checkboxValue($event) })" />
              <span>Responsable del registro
                <span class="block text-[11px] text-gray-400 dark:text-gray-500">
                  Se resuelve al ejecutarse; si no hay responsable, se avisa a un administrador.
                </span>
              </span>
            </label>

            @if (isTicketWorkflow()) {
              <label class="flex items-start gap-2 text-xs text-gray-700 dark:text-gray-300">
                <input type="checkbox" class="mt-0.5 rounded border-gray-300"
                       [checked]="notifRecipients().includeFollowers" [disabled]="readOnly()"
                       (change)="writeNotifRecipients({ includeFollowers: checkboxValue($event) })" />
                <span>Seguidores del ticket</span>
              </label>
            }

            <div>
              <p class="mb-1 text-[11px] font-medium text-gray-600 dark:text-gray-400">Equipo</p>
              <select [class]="fieldClass" [value]="firstTeamId()" [disabled]="readOnly()"
                      (change)="writeNotifRecipients({ teamIds: teamIdList(inputValue($event)) })">
                <option value="">Sin equipo</option>
                @for (t of teams(); track t.id) { <option [value]="t.id">{{ t.name }}</option> }
                @if (firstTeamId() && !teamKnown()) { <option [value]="firstTeamId()">Equipo #{{ firstTeamId() }}</option> }
              </select>
            </div>

            <div>
              <p class="mb-1 text-[11px] font-medium text-gray-600 dark:text-gray-400">
                Usuarios del CRM y correos sueltos
              </p>
              <app-wf-recipient-picker
                [userIds]="notifRecipients().userIds" [emails]="notifRecipients().emails" [disabled]="readOnly()"
                (userIdsChange)="writeNotifRecipients({ userIds: $event })"
                (emailsChange)="writeNotifRecipients({ emails: $event })" />
            </div>

            @if (otherNodes().length) {
              <div>
                <p class="mb-1 text-[11px] font-medium text-gray-600 dark:text-gray-400">
                  El usuario que resulte de un paso
                </p>
                @for (n of otherNodes(); track n.nodeKey) {
                  <label class="flex items-center gap-2 text-xs text-gray-700 dark:text-gray-300">
                    <input type="checkbox" class="rounded border-gray-300"
                           [checked]="isStepUser(n.nodeKey)" [disabled]="readOnly()"
                           (change)="toggleStepUser(n.nodeKey, checkboxValue($event))" />
                    {{ n.title || n.nodeKey }}
                  </label>
                }
                <p [class]="hintClass">
                  Por ejemplo el propietario que dejó «Asignar propietario»: se lee de la salida de ese paso.
                </p>
              </div>
            }

            @if (!hasAnyNotifRecipient()) {
              <p class="rounded-lg bg-error-50 px-2 py-1.5 text-[11px] text-error-600 dark:bg-error-500/10 dark:text-error-400">
                Marca al menos un destinatario.
              </p>
            }
          </div>

          <!--
            De qué pasos informa el aviso. Por defecto ninguno: el mensaje se escribe a mano y el
            nodo se comporta como siempre.
          -->
          @if (otherNodes().length) {
            <div class="space-y-2 rounded-lg border border-gray-200 p-2.5 dark:border-gray-800">
              <label class="text-xs font-medium text-gray-700 dark:text-gray-300">Informar de estos pasos</label>
              @for (n of otherNodes(); track n.nodeKey) {
                <label class="flex items-center gap-2 text-xs text-gray-700 dark:text-gray-300">
                  <input type="checkbox" class="rounded border-gray-300"
                         [checked]="isReportStep(n.nodeKey)" [disabled]="readOnly()"
                         (change)="toggleReportStep(n.nodeKey, checkboxValue($event))" />
                  {{ n.title || n.nodeKey }}
                </label>
              }
              <label class="flex items-center gap-2 text-xs text-gray-700 dark:text-gray-300">
                <input type="checkbox" class="rounded border-gray-300"
                       [checked]="bool('includeStepSummary')" [disabled]="readOnly()"
                       (change)="patchConfig({ includeStepSummary: checkboxValue($event) })" />
                Añadir un resumen de esos pasos al mensaje
              </label>
              <div>
                <label [class]="labelClass">Notificar</label>
                <select [class]="fieldClass" [value]="str('notifyWhen', 'ALWAYS')" [disabled]="readOnly()"
                        (change)="patchConfig({ notifyWhen: inputValue($event) })">
                  <option value="ALWAYS">Siempre que llegue a este paso</option>
                  <option value="ON_FAILURE">Solo si alguno de esos pasos falló</option>
                </select>
              </div>
              <p [class]="hintClass">
                En el mensaje puedes citar un paso concreto con {{ '{{' }}steps.clave.campo{{ '}}' }},
                por ejemplo el id de la tarea que creó un paso anterior.
              </p>
            </div>
          }
          <div>
            <div class="mb-1.5 flex items-center justify-between">
              <label class="text-xs font-medium text-gray-700 dark:text-gray-300">Asunto (opcional)</label>
              <app-wf-variable-picker [target]="subjectField()?.nativeElement ?? null" [disabled]="readOnly()"
                                      (valueChange)="patchConfig({ subject: $event })" />
            </div>
            <input #subjectInput type="text" [class]="fieldClass" [value]="str('subject')" [disabled]="readOnly()"
                   (input)="patchConfig({ subject: inputValue($event) })" />
          </div>
          <!--
            El canal sube por encima del mensaje: es lo que decide con QUÉ editor se escribe
            (INC_CRM_020 (13) inc59). Elegirlo después obligaba a redactar primero y descubrir
            luego que el campo no daba formato.
          -->
          <div>
            <label [class]="labelClass">Canal</label>
            <select [class]="fieldClass" [value]="str('channel', 'IN_APP')" [disabled]="readOnly()"
                    (change)="patchConfig({ channel: inputValue($event) })">
              @for (c of notifChannels; track c.value) { <option [value]="c.value">{{ c.label }}</option> }
            </select>
          </div>
          @if (str('channel', 'IN_APP') === 'EMAIL') {
            <div>
              <label [class]="labelClass">Mensaje</label>
              <app-rich-text-editor
                [value]="messageHtml()" [disabled]="readOnly()" minHeight="200px"
                [variableNamespaces]="variableNamespaces()" [uploadHandler]="imageUploadHandler"
                placeholder="Escribe el mensaje del correo…"
                (valueChange)="patchConfig({ message: $event })" />
            </div>
          } @else {
            <!--
              El aviso dentro del CRM (la campana) es una línea de texto plano: darle un editor
              enriquecido guardaría etiquetas HTML que la campana enseñaría en crudo.
            -->
            <div>
              <div class="mb-1.5 flex items-center justify-between">
                <label class="text-xs font-medium text-gray-700 dark:text-gray-300">Mensaje</label>
                <app-wf-variable-picker [target]="messageField()?.nativeElement ?? null" [disabled]="readOnly()"
                                        (valueChange)="patchConfig({ message: $event })" />
              </div>
              <textarea #messageInput rows="4" [class]="fieldClass" [value]="str('message')" [disabled]="readOnly()"
                        (input)="patchConfig({ message: inputValue($event) })"></textarea>
            </div>
          }
          <ng-container [ngTemplateOutlet]="continueOnError" />
        </div>
      }

      @case ('SEND_TEAMS_NOTIFICATION') {
        <div class="space-y-4">
          <div>
            <label [class]="labelClass">Canal de Teams (id)</label>
            <input type="text" [class]="fieldClass" [value]="str('teamChannelId')" [disabled]="readOnly()"
                   (input)="patchConfig({ teamChannelId: inputValue($event) })" />
          </div>
          <div>
            <label [class]="labelClass">Webhook URL (opcional)</label>
            <input type="text" [class]="fieldClass" [value]="str('webhookUrl')" [disabled]="readOnly()"
                   (input)="patchConfig({ webhookUrl: inputValue($event) })" />
          </div>
          <div>
            <div class="mb-1.5 flex items-center justify-between">
              <label class="text-xs font-medium text-gray-700 dark:text-gray-300">Mensaje</label>
              <app-wf-variable-picker [target]="messageField()?.nativeElement ?? null" [disabled]="readOnly()"
                                      (valueChange)="patchConfig({ message: $event })" />
            </div>
            <textarea #messageInput rows="4" [class]="fieldClass" [value]="str('message')" [disabled]="readOnly()"
                      (input)="patchConfig({ message: inputValue($event) })"></textarea>
          </div>
          <ng-container [ngTemplateOutlet]="continueOnError" />
        </div>
      }

      @case ('SEND_SLACK_NOTIFICATION') {
        <div class="space-y-4">
          <div>
            <label [class]="labelClass">Canal</label>
            <input type="text" [class]="fieldClass" [value]="str('channel', '#ventas')" [disabled]="readOnly()"
                   (input)="patchConfig({ channel: inputValue($event) })" />
          </div>
          <div>
            <div class="mb-1.5 flex items-center justify-between">
              <label class="text-xs font-medium text-gray-700 dark:text-gray-300">Mensaje</label>
              <app-wf-variable-picker [target]="messageField()?.nativeElement ?? null" [disabled]="readOnly()"
                                      (valueChange)="patchConfig({ message: $event })" />
            </div>
            <textarea #messageInput rows="4" [class]="fieldClass" [value]="str('message')" [disabled]="readOnly()"
                      (input)="patchConfig({ message: inputValue($event) })"></textarea>
          </div>
          <ng-container [ngTemplateOutlet]="continueOnError" />
        </div>
      }

      @case ('SEND_SMS') {
        <div class="space-y-4">
          <div>
            <label [class]="labelClass">Propiedad del teléfono</label>
            <app-wf-property-select
              [value]="str('phoneProperty', 'phone')" [options]="propertyOptions()" [disabled]="readOnly()"
              placeholder="Elige una propiedad…"
              (valueChange)="patchConfig({ phoneProperty: $event })"></app-wf-property-select>
          </div>
          <div>
            <div class="mb-1.5 flex items-center justify-between">
              <label class="text-xs font-medium text-gray-700 dark:text-gray-300">Mensaje</label>
              <app-wf-variable-picker [target]="messageField()?.nativeElement ?? null" [disabled]="readOnly()"
                                      (valueChange)="patchConfig({ message: $event })" />
            </div>
            <textarea #messageInput rows="4" [class]="fieldClass" [value]="str('message')" [disabled]="readOnly()"
                      (input)="patchConfig({ message: inputValue($event) })"></textarea>
          </div>
          <label class="flex items-center gap-2 text-xs text-gray-700 dark:text-gray-300">
            <input type="checkbox" class="rounded border-gray-300" [checked]="bool('respectConsent', true)" [disabled]="readOnly()"
                   (change)="patchConfig({ respectConsent: checkboxValue($event) })" />
            Respetar el consentimiento
          </label>
          <ng-container [ngTemplateOutlet]="continueOnError" />
        </div>
      }

      @case ('SEND_WHATSAPP') {
        <div class="space-y-4">
          <div>
            <label [class]="labelClass">Propiedad del teléfono</label>
            <app-wf-property-select
              [value]="str('phoneProperty', 'phone')" [options]="propertyOptions()" [disabled]="readOnly()"
              placeholder="Elige una propiedad…"
              (valueChange)="patchConfig({ phoneProperty: $event })"></app-wf-property-select>
          </div>
          <div>
            <label [class]="labelClass">Nombre de plantilla</label>
            <input type="text" [class]="fieldClass" [value]="str('templateName')" [disabled]="readOnly()"
                   (input)="patchConfig({ templateName: inputValue($event) })" />
          </div>
          <div>
            <label [class]="labelClass">Parámetros (JSON)</label>
            <textarea rows="3" [class]="jsonAreaClass" [value]="jsonOf('parameters')" [disabled]="readOnly()"
                      (change)="setJson('parameters', inputValue($event))"></textarea>
          </div>
          <label class="flex items-center gap-2 text-xs text-gray-700 dark:text-gray-300">
            <input type="checkbox" class="rounded border-gray-300" [checked]="bool('respectConsent', true)" [disabled]="readOnly()"
                   (change)="patchConfig({ respectConsent: checkboxValue($event) })" />
            Respetar el consentimiento
          </label>
          <ng-container [ngTemplateOutlet]="continueOnError" />
        </div>
      }
    }

    <!-- CC / CCO: mismo control de usuarios + correos, plegado para no saturar el panel. -->
    <ng-template #ccBlock>
      <details class="rounded-lg border border-gray-200 px-2.5 py-2 dark:border-gray-800">
        <summary class="flex cursor-pointer items-center gap-2 text-xs font-medium text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200">
          CC (con copia)
          @if (ccCount()) { <span [class]="countBadgeClass">{{ ccCount() }}</span> }
        </summary>
        <div class="mt-2">
          <app-wf-recipient-picker
            [userIds]="cc().userIds" [emails]="cc().emails" [disabled]="readOnly()"
            (userIdsChange)="writeAddressList('cc', { userIds: $event })"
            (emailsChange)="writeAddressList('cc', { emails: $event })" />
        </div>
      </details>
    </ng-template>

    <ng-template #bccBlock>
      <details class="rounded-lg border border-gray-200 px-2.5 py-2 dark:border-gray-800">
        <summary class="flex cursor-pointer items-center gap-2 text-xs font-medium text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200">
          CCO (copia oculta)
          @if (bccCount()) { <span [class]="countBadgeClass">{{ bccCount() }}</span> }
        </summary>
        <div class="mt-2">
          <app-wf-recipient-picker
            [userIds]="bcc().userIds" [emails]="bcc().emails" [disabled]="readOnly()"
            (userIdsChange)="writeAddressList('bcc', { userIds: $event })"
            (emailsChange)="writeAddressList('bcc', { emails: $event })" />
        </div>
      </details>
    </ng-template>

    <!-- emailProperty: opción AVANZADA. Mostrarla siempre hacía creer que había que adivinar
         el nombre de la propiedad para poder enviar. -->
    <ng-template #advancedRecipientBlock>
      <details class="rounded-lg border border-gray-200 px-2.5 py-2 dark:border-gray-800">
        <summary class="cursor-pointer text-xs font-medium text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200">
          Opciones avanzadas
        </summary>
        <div class="mt-2 space-y-1.5">
          <label [class]="labelClass">Propiedad de la que se toma el correo del contacto</label>
          <app-wf-property-select
            [value]="recipients().emailProperty" [options]="propertyOptions()" [disabled]="readOnly()"
            placeholder="Correo (email)"
            (valueChange)="writeEmailProperty($event)"></app-wf-property-select>
          <p [class]="hintClass">
            Por defecto «email». Cámbiala solo si el correo al que quieres escribir está guardado en otra propiedad
            del registro.
          </p>
        </div>
      </details>
    </ng-template>

    <ng-template #continueOnError>
      <label class="flex items-center gap-2 border-t border-gray-100 pt-3 text-xs text-gray-700 dark:border-gray-800 dark:text-gray-300">
        <input type="checkbox" class="rounded border-gray-300" [checked]="bool('continueOnError')" [disabled]="readOnly()"
               (change)="patchConfig({ continueOnError: checkboxValue($event) })" />
        Continuar el flujo si esta acción falla
      </label>
    </ng-template>
  `
})
export class WorkflowCommunicationActionConfigComponent extends WorkflowNodeConfigBase implements OnInit {
  private readonly emailTemplateApi = inject(WorkflowEmailTemplateApiService);
  private readonly directory = inject(UserDirectoryApiService);
  private readonly teamApi = inject(ChatTeamLookupApiService);

  protected readonly notifRecipientTypes = NOTIF_RECIPIENT_TYPE_OPTIONS;
  protected readonly notifChannels = NOTIF_CHANNEL_OPTIONS;
  protected readonly subtype = computed(() => this.node().nodeSubtype ?? '');
  protected readonly templateName = signal('');

  // SEND_INTERNAL_NOTIFICATION → destinatario USER (A2) / TEAM (A3)
  /**
   * Nombre del usuario destinatario guardado, resuelto por la caché del directorio. Un usuario
   * dado de baja se marca en vez de esconderse: explica por qué ya no recibe la notificación.
   */
  protected readonly recipientUserName = computed(() => {
    const id = this.num('recipientUserId');
    if (!id) {
      return '';
    }
    const user = this.directory.knownUsers().get(id);
    if (!user) {
      return this.directory.unresolvedUserIds().has(id) ? `Usuario #${id} (no disponible)` : '';
    }
    return `${directoryUserLabel(user)}${user.isActive === false ? ' (inactivo)' : ''}`;
  });
  protected readonly teams = signal<TeamLookupResponse[]>([]);
  protected readonly teamKnown = computed(() => this.teams().some((t) => String(t.id) === this.str('teamId')));

  /** Propiedades sugeridas del objeto base del workflow (etiqueta legible + clave). */
  protected readonly propertyOptions = computed(() => buildPropertyOptions(this.editor.workflow()?.objectType ?? null));

  // ── Cuerpo del correo con formato (INC_CRM_020 (13) inc59) ──────────────────
  //
  // «Cuerpo» era un <textarea> mientras el motor manda ese texto TAL CUAL como `bodyHtml` del
  // correo (`WorkflowCommunicationActionService.dispatchUserEmail`): quien quisiera una negrita o
  // un enlace tenía que escribir la etiqueta HTML a mano, y quien no lo supiera enviaba un muro
  // de texto sin saltos de línea siquiera. Se reutiliza el editor del compositor de campañas, que
  // ya resuelve negrita/cursiva/subrayado, listas, enlaces, encabezados y vista de código.

  /**
   * Config guardada → HTML para el editor.
   *
   * Los nodos configurados ANTES de este cambio guardaron texto plano: si se inyectara tal cual
   * como `innerHTML`, los saltos de línea desaparecerían y el usuario vería su mensaje en un solo
   * párrafo. Se convierte solo cuando el valor NO trae marcado (no se toca lo que ya es HTML) y la
   * conversión es de PINTADO: la config no se reescribe hasta que el usuario edita de verdad.
   */
  protected readonly bodyHtml = computed(() => plainTextToHtml(this.str('body')));
  protected readonly messageHtml = computed(() => plainTextToHtml(this.str('message')));

  /**
   * Tokens `{{...}}` del workflow servidos al menú de variables del editor.
   *
   * El `app-wf-variable-picker` inserta en un <input>/<textarea> por posición del cursor y no
   * sabe escribir dentro de un contenteditable; el editor rico trae su propio menú, alimentado
   * por este mapeo. Es el MISMO catálogo (`WorkflowEditorService.variables`), así que las dos
   * vistas ofrecen exactamente los mismos tokens.
   */
  protected readonly variableNamespaces = computed<AvailableVariableNamespace[]>(() => {
    const groups = new Map<string, AvailableVariableNamespace>();
    for (const variable of this.editor.variables()) {
      const namespace = variable.token.includes('.') ? variable.token.slice(0, variable.token.indexOf('.')) : 'general';
      let group = groups.get(namespace);
      if (!group) {
        group = { name: namespace, displayName: WORKFLOW_VARIABLE_GROUP_LABELS[namespace] ?? namespace, variables: [] };
        groups.set(namespace, group);
      }
      group.variables.push({ key: variable.token, label: variable.label, type: 'STRING' });
    }
    return [...groups.values()];
  });

  /**
   * Imágenes del cuerpo: se suben al Asset Manager de plantillas y quedan como <img src> con URL
   * pública, que es lo único que un cliente de correo sabe pintar. Mismo handler que el compositor
   * de campañas — sin él, el editor simplemente no muestra el botón de imagen.
   */
  readonly imageUploadHandler = (file: File): Promise<string> =>
    this.templateApi.uploadAsset(file).toPromise().then((res) => res!.url);

  // Campos destino del variable-picker. Solo uno de cada nombre existe a la vez (el @switch
  // renderiza un único caso), así que un viewChild por concepto resuelve el campo visible.
  protected readonly subjectField = viewChild<ElementRef<HTMLInputElement>>('subjectInput');
  protected readonly messageField = viewChild<ElementRef<HTMLTextAreaElement>>('messageInput');
  protected readonly jsonAreaClass =
    'w-full rounded-lg border border-gray-300 px-2 py-1.5 font-mono text-[11px] text-gray-800 focus:border-brand-500 focus:outline-none disabled:opacity-60 dark:border-gray-700 dark:bg-gray-950 dark:text-white/90';

  private readonly templateApi = inject(TemplateApiService);

  private allTemplates: EmailTemplateResponse[] = [];

  constructor() {
    super();
    effect(() => {
      const id = this.num('emailTemplateId');
      if (!id) {
        this.templateName.set('');
        return;
      }
      const cached = this.allTemplates.find((t) => t.id === id);
      if (cached) {
        this.templateName.set(this.templateLabel(cached));
        return;
      }
      this.emailTemplateApi.list().subscribe({
        next: (list) => {
          this.allTemplates = list;
          const found = list.find((t) => t.id === id);
          if (found) {
            this.templateName.set(this.templateLabel(found));
          }
        },
        error: () => {}
      });
    });
    // A2: pedir al directorio el nombre del usuario destinatario guardado (modo `ids`).
    effect(() => {
      const id = this.num('recipientUserId');
      if (id) {
        this.directory.resolveUsers([id]);
      }
    });
  }

  ngOnInit(): void {
    // A3: catálogo de equipos para el destinatario TEAM.
    this.teamApi.getTeams({ activeOnly: true }).subscribe({
      next: (list) => this.teams.set(list),
      error: () => {}
    });
  }

  // ── A2: buscador de usuarios (directorio unificado, búsqueda en servidor) ────
  userSearch = (term: string): Observable<EntitySelectPage<DirectoryUser>> =>
    this.directory.searchUsers(term, 20).pipe(
      map((list) => ({ items: list, totalPages: 1 })),
      catchError(() => of({ items: [] as DirectoryUser[], totalPages: 0 }))
    );
  userLabel = (user: DirectoryUser): string => directoryUserLabel(user);
  userSublabel = (user: DirectoryUser): string => user.mail ?? '';
  protected onRecipientUserSelected(user: DirectoryUser | null): void {
    this.directory.remember(user);
  }

  // ── Destinatarios de email (Para / CC / CCO) ────────────────────────────────

  protected readonly objectType = computed(() => this.editor.workflow()?.objectType ?? null);
  /**
   * Destinatarios de la notificación interna leídos de la forma canónica y, si no existe, de la
   * antigua (`recipientType` + su campo). Migrar en la lectura evita tocar nodos ya guardados
   * hasta que alguien los edita.
   */
  protected readonly notifRecipients = computed<NotifRecipientsModel>(() => {
    const cfg = this.config();
    const modern = cfg['recipients'];
    if (modern && typeof modern === 'object' && !Array.isArray(modern)) {
      const obj = modern as Record<string, unknown>;
      return {
        includeOwner: obj['includeOwner'] === true,
        includeFollowers: obj['includeFollowers'] === true,
        userIds: this.numberList(obj['userIds']),
        teamIds: this.numberList(obj['teamIds']),
        emails: this.emailList(obj['emails']),
        stepUserKeys: this.stringList(obj['stepUserKeys'])
      };
    }
    const tipo = this.str('recipientType', 'OWNER');
    const userId = this.num('recipientUserId');
    const teamId = this.num('teamId') || this.num('recipientTeamId');
    const mail = this.str('recipientEmail');
    return {
      includeOwner: tipo === 'OWNER',
      includeFollowers: false,
      userIds: tipo === 'USER' && userId ? [userId] : [],
      teamIds: tipo === 'TEAM' && teamId ? [teamId] : [],
      emails: tipo === 'EMAIL' && mail ? [mail] : [],
      stepUserKeys: []
    };
  });

  protected writeNotifRecipients(patch: Partial<NotifRecipientsModel>): void {
    const next = { ...this.notifRecipients(), ...patch };
    const config = { ...this.config() };
    // Al escribir la forma nueva se retiran las claves de la vieja: dejarlas haría que el backend
    // tuviera dos fuentes de verdad para el mismo dato.
    delete config['recipientType'];
    delete config['recipientUserId'];
    delete config['recipientEmail'];
    delete config['recipientTeamId'];
    delete config['teamId'];
    config['recipients'] = { ...next };
    this.applyConfig(config);
  }

  protected readonly hasAnyNotifRecipient = computed(() => {
    const r = this.notifRecipients();
    return r.includeOwner || r.includeFollowers || r.userIds.length > 0
      || r.teamIds.length > 0 || r.emails.length > 0 || r.stepUserKeys.length > 0;
  });

  protected firstTeamId(): string {
    const ids = this.notifRecipients().teamIds;
    return ids.length ? String(ids[0]) : '';
  }

  protected teamIdList(raw: string): number[] {
    const parsed = Number(raw);
    return raw.trim() && Number.isFinite(parsed) ? [parsed] : [];
  }

  /** Los demás nodos del flujo: de ellos se puede informar y de ellos sale un usuario. */
  protected readonly otherNodes = computed(() =>
    this.editor.nodes()
      .filter((n) => n.nodeKey !== this.node().nodeKey && n.nodeType !== 'ANNOTATION')
      .map((n) => ({ nodeKey: n.nodeKey, title: n.title ?? '' }))
  );

  protected isReportStep(nodeKey: string): boolean {
    return this.stringList(this.config()['reportSteps']).includes(nodeKey);
  }

  protected toggleReportStep(nodeKey: string, checked: boolean): void {
    const current = this.stringList(this.config()['reportSteps']);
    const next = checked
      ? (current.includes(nodeKey) ? current : [...current, nodeKey])
      : current.filter((key) => key !== nodeKey);
    this.patchConfig({ reportSteps: next });
  }

  protected isStepUser(nodeKey: string): boolean {
    return this.notifRecipients().stepUserKeys.includes(nodeKey);
  }

  protected toggleStepUser(nodeKey: string, checked: boolean): void {
    const current = this.notifRecipients().stepUserKeys;
    const next = checked
      ? (current.includes(nodeKey) ? current : [...current, nodeKey])
      : current.filter((key) => key !== nodeKey);
    this.writeNotifRecipients({ stepUserKeys: next });
  }

  private stringList(raw: unknown): string[] {
    return Array.isArray(raw)
      ? raw.map((item) => String(item ?? '').trim()).filter((item) => item !== '')
      : [];
  }

  protected readonly isContactWorkflow = computed(() => this.objectType() === 'CONTACT');
  /** Los seguidores solo existen en tickets; en otro objeto la casilla ni se ofrece. */
  protected readonly isTicketWorkflow = computed(() => this.objectType() === 'TICKET');
  protected readonly objectTypeLabel = computed(() => {
    const type = this.objectType();
    return type ? (WORKFLOW_OBJECT_TYPE_LABELS[type] ?? type) : 'otro objeto';
  });

  protected readonly countBadgeClass =
    'rounded-full bg-brand-50 px-1.5 py-0.5 text-[10px] font-semibold text-brand-600 dark:bg-brand-500/15 dark:text-brand-300';

  /**
   * "Para" normalizado desde CUALQUIER forma aceptada por el backend:
   * `recipients` objeto (canónica) · `recipients` array de correos · `recipient` objeto ·
   * `recipient` string (propiedad o correo) · ausente (= contacto del registro con `email`).
   */
  protected readonly recipients = computed<WorkflowRecipientsModel>(() => {
    const cfg = this.config();
    const modern = cfg['recipients'];
    if (Array.isArray(modern)) {
      return { includeRecordContact: false, includeRecordOwner: false, includeFollowers: false,
        emailProperty: 'email', userIds: [], emails: this.emailList(modern) };
    }
    if (modern && typeof modern === 'object') {
      const obj = modern as Record<string, unknown>;
      return {
        // Omitir includeRecordContact equivale a TRUE (compatibilidad con el modelo viejo).
        includeRecordContact: typeof obj['includeRecordContact'] === 'boolean' ? obj['includeRecordContact'] : true,
        // Los dinámicos son opt-in: un nodo guardado antes no empieza a escribir a nadie nuevo.
        includeRecordOwner: obj['includeRecordOwner'] === true,
        includeFollowers: obj['includeFollowers'] === true,
        emailProperty: this.propertyOrDefault(obj['emailProperty']),
        userIds: this.numberList(obj['userIds']),
        emails: this.emailList(obj['emails'])
      };
    }
    const legacy = cfg['recipient'];
    if (legacy && typeof legacy === 'object' && !Array.isArray(legacy)) {
      const obj = legacy as Record<string, unknown>;
      return {
        includeRecordContact: true,
        includeRecordOwner: false,
        includeFollowers: false,
        emailProperty: this.propertyOrDefault(obj['emailProperty']),
        userIds: [],
        emails: []
      };
    }
    if (typeof legacy === 'string' && legacy.trim()) {
      const value = legacy.trim();
      return value.includes('@')
        ? { includeRecordContact: false, includeRecordOwner: false, includeFollowers: false,
            emailProperty: 'email', userIds: [], emails: [value] }
        : { includeRecordContact: true, includeRecordOwner: false, includeFollowers: false,
            emailProperty: value, userIds: [], emails: [] };
    }
    return { includeRecordContact: true, includeRecordOwner: false, includeFollowers: false,
      emailProperty: 'email', userIds: [], emails: [] };
  });

  protected readonly hasAnyRecipient = computed(() => {
    const to = this.recipients();
    // Los dinamicos cuentan: un nodo que solo avisa al responsable o a los seguidores es valido,
    // y la validacion del backend usa el mismo criterio.
    return to.includeRecordContact || to.includeRecordOwner || to.includeFollowers
      || to.userIds.length > 0 || to.emails.length > 0;
  });

  protected readonly cc = computed(() => this.addressList('cc'));
  protected readonly bcc = computed(() => this.addressList('bcc'));
  protected readonly ccCount = computed(() => this.cc().userIds.length + this.cc().emails.length);
  protected readonly bccCount = computed(() => this.bcc().userIds.length + this.bcc().emails.length);

  /**
   * Escribe SIEMPRE la forma canónica `recipients` y elimina la clave antigua `recipient`:
   * así un nodo viejo se migra en cuanto se toca y nunca quedan las dos formas a la vez.
   */
  protected writeRecipients(patch: Partial<WorkflowRecipientsModel>): void {
    const next = { ...this.recipients(), ...patch };
    const config = { ...this.config() };
    delete config['recipient'];
    config['recipients'] = {
      includeRecordContact: next.includeRecordContact,
      includeRecordOwner: next.includeRecordOwner,
      includeFollowers: next.includeFollowers,
      emailProperty: next.emailProperty || 'email',
      userIds: next.userIds,
      emails: next.emails
    };
    this.applyConfig(config);
  }

  /**
   * En marketing el backend solo admite el contacto del registro como "Para": al tocar la
   * propiedad se normaliza a esa forma (y se descartan usuarios/correos que hubiera heredado
   * el nodo), para que la validación del builder no falle por una config imposible.
   */
  protected writeEmailProperty(value: string): void {
    if (this.subtype() === 'SEND_MARKETING_EMAIL') {
      this.writeRecipients({ emailProperty: value, includeRecordContact: true, userIds: [], emails: [] });
      return;
    }
    this.writeRecipients({ emailProperty: value });
  }

  protected writeAddressList(key: 'cc' | 'bcc', patch: Partial<WorkflowAddressListModel>): void {
    const next = { ...this.addressList(key), ...patch };
    this.patchConfig({ [key]: { userIds: next.userIds, emails: next.emails } });
  }

  /** CC/CCO normalizado: objeto canónico, array de correos o string suelto. */
  private addressList(key: 'cc' | 'bcc'): WorkflowAddressListModel {
    const value = this.config()[key];
    if (Array.isArray(value) || typeof value === 'string') {
      return { userIds: [], emails: this.emailList(value) };
    }
    if (value && typeof value === 'object') {
      const obj = value as Record<string, unknown>;
      return { userIds: this.numberList(obj['userIds']), emails: this.emailList(obj['emails']) };
    }
    return { userIds: [], emails: [] };
  }

  private numberList(value: unknown): number[] {
    return Array.isArray(value)
      ? value.map((v) => Number(v)).filter((n) => Number.isFinite(n) && n > 0)
      : [];
  }

  private emailList(value: unknown): string[] {
    if (Array.isArray(value)) {
      return value.map((v) => String(v).trim()).filter(Boolean);
    }
    if (typeof value === 'string') {
      return value.split(',').map((s) => s.trim()).filter(Boolean);
    }
    return [];
  }

  private propertyOrDefault(value: unknown): string {
    const text = value == null ? '' : String(value).trim();
    return text || 'email';
  }

  protected numberOrNull(value: string): number | null {
    const n = Number(value);
    return value.trim() && Number.isFinite(n) ? n : null;
  }

  protected onTemplateSelected(t: EmailTemplateResponse | null): void {
    this.templateName.set(t ? this.templateLabel(t) : '');
  }

  templateSearch = (term: string): Observable<EntitySelectPage<EmailTemplateResponse>> => {
    return this.emailTemplateApi.list().pipe(
      map((list) => {
        this.allTemplates = list;
        const t = term.trim().toLowerCase();
        const items = t
          ? list.filter((x) => `${x.subject ?? ''} ${x.purpose ?? ''}`.toLowerCase().includes(t))
          : list;
        return { items, totalPages: 1 };
      }),
      catchError(() => of({ items: [] as EmailTemplateResponse[], totalPages: 0 }))
    );
  };
  templateLabel = (t: EmailTemplateResponse): string => t.subject || t.purpose;
  templateSublabel = (t: EmailTemplateResponse): string => `${t.purpose}${t.locale ? ' · ' + t.locale : ''} v${t.version}`;
}

/** Etiquetas legibles de los grupos de tokens, por prefijo del token. */
const WORKFLOW_VARIABLE_GROUP_LABELS: Readonly<Record<string, string>> = {
  contact: 'Contacto',
  company: 'Empresa',
  deal: 'Negocio',
  quote: 'Cotización',
  ticket: 'Ticket',
  workflow: 'Workflow',
  enrollment: 'Inscripción',
  object: 'Registro inscrito',
  latestStep: 'Paso anterior'
};

/**
 * Texto plano heredado → HTML equivalente. Si el valor ya trae una etiqueta, se devuelve intacto:
 * volver a escapar HTML real dejaría al usuario viendo `<b>` en su correo.
 */
function plainTextToHtml(raw: string): string {
  if (raw === '' || /<[a-z!/][^>]*>/i.test(raw)) {
    return raw;
  }
  return raw
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/\r\n|\r|\n/g, '<br>');
}
